import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Divider, Fab, Grid, Paper, TextField, Typography } from "@mui/material";
import { Delete, Info, Search } from "@mui/icons-material";
import { AxiosError } from "axios/index";
import dayjs from "dayjs";

import { BaseService } from "../../../../../app/services/base-service";
import { URLS } from "../../../../../app/services/app-urls";
import { translate } from "../../../../../app/translate/translate";

import { Analysis } from "../../../../../app/models/general/analysis";
import { Forecast } from "../../../../../app/models/general/forecast";
import { ForecastComponent } from "../../../../../app/models/general/forecast-component";
import { PaginatedResult } from "../../../../../app/models/default/paginated-result";

import { ErrorComponent } from "../../../../../app/components/error/error.component";
import TablePaginationComponent from "../../../../../app/components/table/table-pagination.component";
import WarningTooltip from "../../../../../app/components/tooltip/warning-tooltip";
import { toast } from "react-toastify";
import DialogVersion from "../../../../../app/components/dialog/dialog.component";
import Loading from "../../../../../app/components/loading/loading";
import EditIcon from "@mui/icons-material/Edit";
import { parseLocaleNumber } from "../../../../../app/utils/locale-number-utils";

const SimulationForecastComponent = ({ analysis }: { analysis: Analysis }) => {
  const service = new BaseService<Forecast>(URLS.FORECAST);
  const analysisService = new BaseService<Forecast>(URLS.ANALYSIS);

  const [searchMaterial, setSearchMaterial] = useState<string>("");
  const [forecastComponents, setForecastComponents] = useState<ForecastComponent[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const [editingCell, setEditingCell] = useState<{ cellKey: string } | null>(null);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSaveCell = async (forecastComponentId: number, newValue: any) => {
    const originalForecastComponents = [...forecastComponents];
    const lang = translate.language === "en" ? "en" : "pt";
    let numericString = newValue;

    if (typeof numericString === "string") {
      if (lang === "en") {
        numericString = numericString.replace(/,/g, "");
      } else {
        numericString = numericString.replace(/\./g, "").replace(",", ".");
      }
    }

    let parsedValue = typeof numericString === "string" ? parseFloat(numericString) : numericString;
    parsedValue = isNaN(parsedValue) ? 0 : parsedValue;

    setForecastComponents(prev =>
      prev.map((row: any) => {
        const dateColumn = Object.keys(row).find(
          key => row[key]?.forecast_component_id === forecastComponentId,
        );

        if (!dateColumn) return row;

        const prevCell = row[dateColumn] ?? {};
        const prevQty = prevCell.qty;

        const nextOriginal =
          prevCell.original_qty === undefined || prevCell.original_qty === null
            ? prevQty
            : prevCell.original_qty;

        const finalParsedValue = isNaN(parsedValue) ? 0 : parsedValue;
        const isActuallyEdited = finalParsedValue !== nextOriginal;

        const newRow = { ...row };
        newRow[dateColumn] = {
          ...prevCell,
          qty: finalParsedValue,
          original_qty: nextOriginal,
          is_edited: isActuallyEdited,
        };
        return newRow;
      }),
    );

    setEditingCell(null);

    try {
      await analysisService.saveFromDetailRoute(
        { forecast_component_id: forecastComponentId, quantity: parsedValue },
        analysis?.id,
        "update-forecast",
      );
      toast.success(translate.t("successfully_updated"));
    } catch (err) {
      setForecastComponents(originalForecastComponents);
      const axiosError = err as AxiosError;
      ErrorComponent(axiosError);
      toast.error(translate.t("update_failed"));
    }
  };

  const filter = () => {
    if (analysis.forecast == undefined) return;

    service.clearParameters();

    service.addParameter("analysis_id", analysis?.id);

    if (searchMaterial) {
      service.addParameter("part_number", searchMaterial);
    }

    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    setLoading(true);
    service.getFromDetailRoutePaginate(analysis.forecast, "components")
      .then((response) => {
        setPagination(response.data);
        setForecastComponents(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    filter();
  }, []);

  useEffect(() => {
    filter();
  }, [page, rowsPerPage]);

  const { displayedColumnsShow, displayedColumns } = useMemo(() => {
    if (forecastComponents && forecastComponents.length > 0) {

      const cleanedComponents = forecastComponents.map((item: any) => {
        const cleanedItem: { [key: string]: any } = {};
        Object.keys(item).forEach(key => {
          cleanedItem[key] = item[key] === undefined || item[key] === null ? 0 : item[key];
        });
        return cleanedItem;
      });

      const firstItem = cleanedComponents[0];

      const fields = Object.keys(firstItem).filter(field => !["is_empty", "bom_id"].includes(field));

      const convertedFields = [...fields.slice(1)];
      const formattedDates = convertedFields.map(dateStr =>
        dayjs(dateStr, "DD/MM/YYYY").format("L"),
      );

      const columns = fields.length > 0 ? ["material", ...formattedDates] : [];

      return { displayedColumnsShow: columns, displayedColumns: fields };
    }
    return { displayedColumnsShow: [], displayedColumns: [] };
  }, [forecastComponents, page, rowsPerPage]);


  const cleanedForecastComponents = useMemo(() => {
    if (!forecastComponents || forecastComponents.length === 0) {
      return [];
    }

    return forecastComponents.map((item: any) => {
      const cleanedItem: any = {};
      Object.keys(item).forEach(key => {
        if (dayjs(key, "DD/MM/YYYY", true).isValid()) {
          const qtyValue = item[key]?.qty;
          cleanedItem[key] = qtyValue === undefined || qtyValue === null ? 0 : qtyValue;
        } else {
          cleanedItem[key] = item[key];
        }
      });
      return cleanedItem;
    });
  }, [forecastComponents]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const headerCellCustomStyleHandler = (column: string) => {
    if (column === "material") {
      return {
        width: 160,
        height: 48,
        position: "sticky",
        left: 0,
        zIndex: 10,
        padding: "8px",
        fontSize: "16px",
      };
    }
    return {
      width: 140,
      height: 48,
      borderWidth: "0 1px 1px 0",
      borderStyle: "solid",
      borderColor: "#e0e0e0",
      backgroundColor: "white",
      color: "#3949AB !important",
      textAlign: "right",
      padding: "8px",
      fontSize: "16px",
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cellCustomStyleHandler = (column: string, _row: any = null) => {
    if (column === "part_number") {
      return {
        position: "sticky",
        left: 0,
        zIndex: 10,
        backgroundColor: "#FAFAFA",
        textAlign: "left",
        padding: "8px",
      };
    }

    return {
      backgroundColor: "white",
      color: "#3949AB",
      textAlign: "right",
      borderWidth: "0 1px 1px 0",
      borderStyle: "solid",
      borderColor: "#e0e0e0",
      padding: "8px",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#F5F5F5",
        "& .eye-icon": {
          opacity: 1,
        },
      },
    };
  };

  const handleIconBomEmpty = (hasBomEmpty: boolean) => {
    return hasBomEmpty && <WarningTooltip messageKey={"bom_not_imported"} />;
  };

  const renderCellHandler = (column: string, index: number, row: any) => {
    if (column === "part_number") {
      return (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onDoubleClick={() => {
            const newTab = window.open(`/general/bom/${row.bom_id}`, "_blank");
            if (newTab) newTab.focus();
          }}
        >
          <Typography variant="body1" id="material">
            {row.part_number}
          </Typography>
          {handleIconBomEmpty(row.is_empty)}
        </Box>
      );
    }

    const safePartNumber = row.part_number.replace(/\./g, "");
    const safeColumn = column.replace(/\//g, "-");
    const cellKey = `${safePartNumber}-${safeColumn}`;
    const isEditing = editingCell?.cellKey === cellKey;

    const currentCellData: any = forecastComponents.find(
      (item: any) => item.part_number === row.part_number,
    );

    const forecastComponentId = currentCellData?.[column]?.forecast_component_id;
    const isEdited = currentCellData?.[column]?.is_edited;
    const originalValue = currentCellData?.[column]?.original_qty;

    const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let v = event.target.value;
      const lang = translate.language === "en" ? "en" : "pt";

      if (lang === "en") {
        v = v.replace(/[^\d.]/g, "");

        const parts = v.split(".");
        let intPart = parts[0].replace(/^0+(?!$)/, "");
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        v = intPart;
        if (parts[1] !== undefined) {
          v += "." + parts[1].replace(/,/g, "");
        }
      } else {
        v = v.replace(/[^\d,]/g, "");

        const parts = v.split(",");
        let intPart = parts[0].replace(/^0+(?!$)/, "");
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        v = intPart;
        if (parts[1] !== undefined) {
          v += "," + parts[1].replace(/\./g, "");
        }
      }

      event.target.value = v;
    };


    if (isEditing) {
      return (
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ width: "100%" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            size="small"
            color="error"
            variant="outlined"
            sx={{ borderColor: "#cfd8dc" }}
            onClick={() => setEditingCell(null)}
          >
            ✕
          </Button>

          <Button
            size="small"
            color="success"
            variant="outlined"
            sx={{ borderColor: "#cfd8dc" }}
            onClick={() =>
              handleSaveCell(
                forecastComponentId,
                (document.querySelector(`#cell-input-${cellKey}`) as HTMLInputElement)?.value,
              )
            }
          >
            ✓
          </Button>

          <Box flex={1}>
            <TextField
              id={`cell-input-${cellKey}`}
              autoFocus
              defaultValue={parseLocaleNumber(row[column], false)}
              variant="standard"
              fullWidth
              size="small"
              inputProps={{ style: { textAlign: "right" } }}
              onChange={handleNumericChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const input = e.target as HTMLInputElement;
                  handleSaveCell(
                    forecastComponentId,
                    input.value,
                  );
                }
              }}
            />
          </Box>
        </Box>
      );
    }

    const fontWeight = isEdited ? 600 : 400;

    return (
      <Box
        onClick={() => setEditingCell({ cellKey })}
        sx={{
          fontWeight: fontWeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
        }}
      >
        {isEdited && (
          <WarningTooltip
            color="var(--blue-gray-300)"
            messageKey={"original_value"}
            additionalText={": " + originalValue}
            IconComponent={Info}
          />
        )}
        {!isEdited && (
          <EditIcon
            className="eye-icon"
            fontSize={"small"}
            sx={{
              color: "#90A4AE",
              opacity: 0,
              transition: "opacity 0.2s",
            }}
          />
        )}

        {typeof row[column] === "number"
          ? parseLocaleNumber(Number(row[column]), false)
          : String(parseLocaleNumber(row[column], false) ?? "")}
      </Box>
    );
  };

  const renderCellHandlers: Record<string, typeof renderCellHandler> = {};
  displayedColumns.forEach((col) => {
    renderCellHandlers[col] = renderCellHandler;
  });

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = (result: unknown) => {
    if (typeof result === "boolean" && result && analysis) {
      analysisService.clearParameters();
      analysisService.deleteFromDetailRoute(analysis?.id, "reset-forecast")
        .then(() => {
          toast.success(translate.t("successfully_reset"));
          filter();
        });
    }
    setOpenDeleteModal(false);
  };

  return (
    <section>
      <Loading open={loading} />
      <Paper variant="outlined" sx={{ padding: "16px", marginBottom: "16px" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2, flex: 1 }}>
            <TextField
              id="inp-forecast-material"
              label={translate.t("material")}
              fullWidth
              value={searchMaterial}
              onChange={(e) => setSearchMaterial(e.target.value)}
              variant="outlined"
              onKeyDown={(e: any) => {
                if (e.key === "Enter") {
                  if (page === 0) {
                    filter();
                  } else {
                    setPage(0);
                  }
                }
              }}
            />

            <Fab
              id="inp-forecast-search-submit"
              size="small"
              type="submit"
              color="primary"
              onClick={() => {
                if (page === 0) {
                  filter();
                } else {
                  setPage(0);
                }
              }}
            >
              <Search />
            </Fab>
          </Grid>

          <Grid item>
            <Divider
              orientation="vertical"
              variant="fullWidth"
              sx={{ height: "40px" }}
            />
          </Grid>

          <Grid item>
            <Button
              startIcon={<Delete height={18} width={18} />}
              variant="outlined"
              color="primary"
              onClick={handleOpenDeleteModal}
            >
              {translate.t("reset")}
            </Button>
          </Grid>

        </Grid>

        <Grid item>
          <Divider
            orientation="horizontal"
            variant="fullWidth"
            sx={{ marginTop: "20px" }}
          />
        </Grid>

        <Grid sx={{ marginTop: "20px" }}>
          <TablePaginationComponent
            state={cleanedForecastComponents}
            displayedColumnsShow={displayedColumnsShow}
            displayedColumns={displayedColumns}
            count={pagination?.count}
            next={pagination?.next}
            previous={pagination?.previous}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            headerCellCustomStyleHandler={headerCellCustomStyleHandler}
            cellCustomStyleHandler={cellCustomStyleHandler}
            renderCellHandler={renderCellHandlers}
            stickyHeader
            disablePointer
          />
        </Grid>
      </Paper>

      {openDeleteModal && (
        <DialogVersion
          id="dialog-reset-forecast"
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          title={translate.t("reset_data")}
          message={translate.t("confirm_reset")}
          labelButton={translate.t("confirm")}
          colorButton={"primary"}
        />
      )}
    </section>
  );
};

export default SimulationForecastComponent;
